<template>
  <v-card flat>
    <!-- Confirm modal -->
    <ConfirmationModal
      ref="confirmationModal"
      :title="confirmationTitle"
      :subtitle="confirmationSubtitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
    />

    <v-row align="center" justify="space-between">
      <v-col cols="12" md="9">
        <Header
          :title="`Employment Information`"
          subtitle="Manage the employment of the customer"
        />
      </v-col>

      <v-col cols="12" md="3" class="text-md-right">
        <v-btn @click="openForm(null)"  text outlined class="shadow-md text-capitalize"
          >
          <v-icon left>mdi-plus</v-icon>
          Create</v-btn
        >
      </v-col>
    </v-row>

    <!-- Empty state -->
    <div
      v-if="!employments || (employments && !employments.length)"
      class="text-center"
    >
      <NoData />
      <p class="mt-5 font-weight-bold">No employments found</p>
      <v-btn
        @click="openForm(null)"
        depressed
        text        
        outlined
        color="primary"
        min-height="38"
        class="text-capitalize"
        >
        <v-icon left>mdi-plus</v-icon>
        Create New</v-btn
      >
    </div>

    <!-- List of items -->
    <div v-else class="mt-5">
      <template v-for="(item, i) in employments">
        <v-card :key="`employment-${i}`" flat outlined class="mb-2">
          <v-card-text>
            <v-row align="center" justify="space-between">
              <v-col cols="12" md="8">
                <div class="d-flex flex-column">
                  <span class="font-weight-medium"
                    >{{
                      item.nameOfEmployer || 'Employer name not available'
                    }}
                    ({{
                      item.isPrimary ? 'Primary Source' : 'Secondary Source'
                    }})</span
                  >
                  <div>
                    <span class="font-weight-medium text-caption">
                      Employment Status:
                    </span>
                    <span class="ml-1 text-caption">
                      {{ item.employmentStatus || 'Not available' }}
                    </span>
                  </div>
                  <div>
                    <span class="font-weight-medium text-caption">
                      Profession:
                    </span>
                    <span class="ml-1 text-caption">
                      {{ item.profession || 'Not available' }}
                    </span>
                  </div>
                  <div>
                    <span class="font-weight-medium text-caption">
                      Industry:
                    </span>
                    <span class="ml-1 text-caption">
                      {{ item.businessIndustry || 'Not available' }}
                    </span>
                  </div>
                  <div>
                    <span class="font-weight-medium text-caption">
                      Sector:
                    </span>
                    <span class="ml-1 text-caption">
                      {{ item.sector || 'Not available' }}
                    </span>
                  </div>
                </div>
              </v-col>
              <v-col class="d-flex justify-end">
                <v-btn
                  small
                  @click="openForm(item, i)"
                  outlined
                  text
                  class="shadow mr-2 mb-2 text-capitalize"
                  >
                  Edit</v-btn
                >
                <v-btn
                  @click="deleteItem(item, i)"
                  small
                  depressed
                  color="error lighten-1"
                  class="shadow text-capitalize"
                  >Delete</v-btn
                >
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>
    </div>

    <ModalFormLayout
      @submit="onSubmit"
      @cancel="onClose"
      ref="employmentModal"
      :options="modalOptions"
    >
      <template #content>
        <ValidationObserver ref="observer">
          <v-row class="mt-2" dense>
            <v-col cols="12">
              <ValidationProvider
                name="Employer Name"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Employer Name"
                  :error-messages="errors"
                  v-model="details.nameOfEmployer"
                >
                  <template v-slot:label>
                    Employer Name
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <ValidationProvider
                name="Main Employer"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-select
                  dense
                  :items="YesNo"
                  outlined
                  placeholder="Is this the main employer?"
                  label="Main Employer"
                  :error-messages="errors"
                  v-model="details.isPrimary"
                >
                  <template v-slot:label>
                    Main Employer
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Employment Status"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  dense
                  :items="employmentStatusSubFactors"
                  outlined
                  label="Employment Status"
                  item-text="subRiskFactorName"
                  item-value="subRiskFactorName"
                  :error-messages="errors"
                  v-model="details.employmentStatus"
                >
                  <template v-slot:label>
                    Employment Status
                    <span class="red--text">*</span>
                  </template>
                </v-autocomplete>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Date Employed"
                :rules="`required|date|date_less_than:${yesterday}`"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  type="date"
                  label="Date Employed"
                  :error-messages="errors"
                  v-model="details.dateEmployed"
                >
                  <template v-slot:label>
                    Date Employed
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Employer Contact"
                rules="required|telephone"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Employer Contact"
                  :error-messages="errors"
                  v-model="details.contactNumber"
                >
                  <template v-slot:label>
                    Employer Contact
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                name="Employer Email"
                rules="email"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  type="email"
                  outlined
                  label="Employer Email"
                  :error-messages="errors"
                  v-model="details.email"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Profession"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  dense
                  outlined
                  :items="professionSubFactors"
                  item-text="subRiskFactorName"
                  item-value="subRiskFactorName"
                  label="Profession"
                  :error-messages="errors"
                  v-model="details.profession"
                >
                  <template v-slot:label>
                    Profession
                    <span class="red--text">*</span>
                  </template>
                </v-autocomplete>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Industry"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  dense
                  :items="industrySubFactors"
                  outlined
                  label="Industry"
                  item-text="subRiskFactorName"
                  item-value="subRiskFactorName"
                  :error-messages="errors"
                  v-model="details.businessIndustry"
                >
                  <template v-slot:label>
                    Industry
                    <span class="red--text">*</span>
                  </template>
                </v-autocomplete>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Sector"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  dense
                  :items="sectorSubFactors"
                  outlined
                  label="Sector"
                  item-text="subRiskFactorName"
                  item-value="subRiskFactorName"
                  :error-messages="errors"
                  v-model="details.sector"
                >
                  <template v-slot:label>
                    Sector
                    <span class="red--text">*</span>
                  </template>
                </v-autocomplete>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Street"
                rules="required|min:3"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Street"
                  :error-messages="errors"
                  v-model="details.street"
                >
                  <template v-slot:label>
                    Street
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="City"
                rules="required|min:2"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="City"
                  :error-messages="errors"
                  v-model="details.city"
                >
                  <template v-slot:label>
                    City
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <ValidationProvider
                name="Country"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <CountryPicker
                  dense
                  outlined
                  required
                  label="Country"
                  :error-messages="errors"
                  v-model="details.country"
                ></CountryPicker>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Province/State"
                rules="required|min:2"
                events="['blur']"
                v-slot="{ errors }"
              >
                <ProvincePicker
                  dense
                  outlined
                  required
                  label="Province/State"
                  :error-messages="errors"
                  v-model="details.state"
                ></ProvincePicker>
              </ValidationProvider>
            </v-col>
          </v-row>
        </ValidationObserver>
      </template>
    </ModalFormLayout>
  </v-card>
</template>

<script>
import pageMixin from '@/mixins/page.mixin';
import Header from './Header.vue';
import { mapGetters, mapActions } from 'vuex';
import { yesterday } from '@/lib/helpers';
import CountryPicker from '@/modules/shared/components/pickers/CountryPicker.vue';
import ProvincePicker from '@/modules/shared/components/pickers/ProvincePicker.vue';

export default {
  components: {
    Header,
    CountryPicker,
    ProvincePicker
  },
  props: {
    //Determine which module is being used
    //and use it to filter the risk factors
    moduleName: {
      type: String,
      default: 'Loan-Pro'
    },

    outlined: {
      type: Boolean,
      default: true
    },

    employments: {
      type: Array,
      default: () => []
    }
  },
  mixins: [pageMixin],
  data() {
    return {
      YesNo: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      yesterday: yesterday(),
      details: {},
      isEditing: false,
      editIndex: -1,

      // Confirmation modal options
      confirmationTitle: '',
      confirmationMessage: '',
      confirmationSubtitle: '',

      modalOptions: {
        title: 'Add an Employment Information',
        subtitle: 'Enter the values and click submit',
        submitButtonText: 'Submit',
        maxWidth: '550px',
        width: '550px',
        color: 'white',
        flat: true
      },

      factors: []
    };
  },

  computed: {
    ...mapGetters('dueDiligenceModule', [
      'dueDiligenceTypes',
      'dueDiligenceGuides'
    ]),
    ...mapGetters('riskFactorModule', ['riskFactorByModuleName']),
    professionSubFactors() {
      return (
        this.riskFactorByModuleName.find(
          (r) => r.riskFactorName == 'Profession'
        )?.subRiskFactors || []
      );
    },
    industrySubFactors() {
      return (
        this.riskFactorByModuleName.find((r) => r.riskFactorName == 'Industry')
          ?.subRiskFactors || []
      );
    },
    employmentStatusSubFactors() {
      return (
        this.riskFactorByModuleName.find(
          (r) => r.riskFactorName == 'Employment Status'
        )?.subRiskFactors || []
      );
    },
    sectorSubFactors() {
      return (
        this.riskFactorByModuleName.find((r) => r.riskFactorName == 'Sector')
          ?.subRiskFactors || []
      );
    }
  },

  emits: ['deleteEmployer', 'update'],

  mounted() {
    this.getRiskFactorByModuleName(this.moduleName);
  },

  methods: {
    ...mapActions('riskFactorModule', ['getRiskFactorByModuleName']),

    // Open the form for creating/editing a new source
    openForm(item, index = -1) {
      this.modalOptions.title = 'Create a employment';
      this.modalOptions.subtitle = 'New employment details will be created';

      this.editIndex = index;
      if (index > -1) {
        this.details = { ...item };
        this.isEditing = true;
        this.modalOptions.title = `Update employment`;
        this.modalOptions.subtitle = `editing employment`;
      }
      this.$refs.employmentModal.open();
    },
    onClose() {
      this.$refs.observer?.reset();
      this.details = {};
      this.isEditing = false;
      this.editIndex = null;
    },
    async onSubmit() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      let employments = structuredClone(this.employments);
      if (this.editIndex > -1) {
        employments = employments.map((e, i) =>
          i == this.editIndex ? this.details : e
        );
      } else {
        employments.push(this.details);
      }

      this.onClose();
      this.$emit('update', employments);
      this.$refs.employmentModal.close();
    },

    deleteItem(item, index = -1) {
      this.confirmationTitle = 'Confirm Delete';
      this.confirmationMessage = 'Are you sure you want to delete this record?';

      this.editIndex = index;
      this.$refs.confirmationModal.show(async () => {
        let employments = structuredClone(this.employments);
        employments = employments.filter((e, i) => i !== this.editIndex);

        this.$emit('update', employments);
      });
    }
  }
};
</script>
